/**
 * 产出形态 —— **合并页的根决策，也是全站唯一一份形态清单**。
 *
 * ---------------------------------------------------------------------------
 * 为什么有这个文件
 * ---------------------------------------------------------------------------
 * 2026-09-29 用户指出侧栏里三条入口其实是一件事：
 *   主图与详情（`/detail-page`）、商品套图（`/ai-set`）、平台图（`/localize`）
 * 三者共用同一批输入（产品信息或成片 + 平台 + 模型 + 张数/比例），
 * 差别只有「产出什么形态」，却被拆成三个页面、三份表单、三套 `jobsFor` 键。
 * 合并后侧栏只留一条入口，形态在页面内用控件切。
 *
 * 合并的具体代价与对策：
 *   1. **app_slug 不能变** —— 它同时是资产分类键（`config/assetTaxonomy.ts`
 *      与后端 `app/taxonomy.py` 的 `_BY_SLUG`）、报价键、后端 `app_kind` 查表键。
 *      所以形态 → app_slug 的映射必须原样保留，写在这里一份，页面与
 *      `lib/appSlug.ts` 都从这里取 —— 两边各写一份就是本项目反复复发的
 *      「同一份清单两处各写一遍」。
 *   2. **右栏取数键收敛成一个** —— `jobsFor('detail-page')` 对全部形态一视同仁。
 *      合并前三个页面各读各的键，用户在套图页看不到详情页的任务，反之亦然。
 *   3. **形态名不是页签名** —— `aplus` 这一档的显示名由**平台档案**决定
 *      （Amazon →「Amazon A+」，淘宝 →「详情页模块」），见
 *      `config/platformDetails.ts` 的 `tabName`。这里只放通用名。
 *
 * ---------------------------------------------------------------------------
 * 三次收敛：7 个形态 → 2 个一级模式 → **不分层，4 个形态平铺**（2026-09-30）
 * ---------------------------------------------------------------------------
 * 第一次（2026-09-29 上午）用户原话「合并为这 2 个就可以」，圈的是一级入口
 * 「电商主图」与「详情页模块」。于是拆成两层概念：
 *   · 一级模式（原 `STUDIO_GROUPS`，2 个）—— 页签外 chip-row，答「来这儿干什么」；
 *   · 产出形态（`STUDIO_MODES`）—— 答「出哪种」。
 *
 * 第二次（2026-09-30）用户原话：「详情页跟主图合并为一页，
 * **然后在输入的时候选择就可以**」。这句把上面那层分组否掉了 ——
 * 判据是「选产出形态不需要任何物料」：一级 chip 与形态控件做的是同一件事
 * （挑一种产出），一个在页签外、一个在页签里，用户要先做 2 选 1 才看得到
 * 完整的形态清单，而那两个选项本来就框不住任何真实约束
 * （平台、张数、比例、必填项都不由它决定）。
 * ⇒ **删掉分组那一层**（`STUDIO_GROUPS` / `getGroup` / `groupOfVariant` /
 *   `variantsOf` / `StudioGroupId` 全部移除），4 个形态平铺在页签 1 的
 *   「产出形态」控件里，一行选完。`STUDIO_MODES` 成为唯一清单、唯一排序。
 *
 * 第三次（2026-09-30）「详情页长图」（`long`）下架，用户原话「详情的长图不搞先」。
 * 「先」字说明是**暂缓**而不是能力被否，所以：
 *   · 只是不再作为可选形态（不渲染、不可提交）；
 *   · `detail-long` 这个 **app_slug 继续保留**在后端 `registry.py` 的 `_BY_SLUG`、
 *     `taxonomy.py` 与 `config/assetTaxonomy.ts` 里 —— 库里还躺着历史任务的产物，
 *     删表会让它们掉进「未分类」（与「下架形态 ≠ 删 app_slug」同一条铁律）；
 *   · 详情页的**逐屏规划能力本身保留**（`aplus` 那一档仍在用 `planDetailShots`），
 *     所以下架它并不需要动提示词层。
 * 要恢复长图：把那条加回 `STUDIO_MODES`（`appSlug: 'detail-long'` 已在后端注册），
 * 并把 `studioShape.ts` 的 `defaultsFor` / `shapeOf` / `ratioChoicesFor` 三处
 * 的 `long` 分支一并加回 —— 那三处现在是按「只剩 main/aplus/copy」写的。
 *
 * 第四次（2026-09-30）「商品套图」（`set` / app_slug `ai-set`）下架，
 * 用户原话对着产出形态控件上的那一项：「**这套图跟详情页重复了**」。
 * 判据是**产出物**：`set` 出的是「一张主图裂变的主图+场景+卖点」，
 * 而 `aplus` 那一档按平台官方模块顺序出的就是同一批东西（还逐张带官方尺寸）——
 * 两个选项摆在一起，用户选哪个都得不到不同的结果，差别只剩计价口径。
 * 下架它同时消掉了一批只有它用的输入：类目 / 场景倾向 / 画面文字（`imageText`）、
 * `SET_CATEGORIES`、`buildSetPrompts` / `buildSetPrompt`（`config/prompts.ts`）、
 * 批量 CSV 的 `category` / `scene` 两列 —— 都是「喂给一条不存在的路」的字段。
 * ⚠️ `ai-set` 这个 **app_slug 仍然留在**后端 `registry.APP_KIND` +
 * `taxonomy._BY_SLUG` + `config/assetTaxonomy.ts` 里（同「下架形态 ≠ 删 app_slug」：
 * 库里还躺着历史任务产出的图，删表会让它们掉进「未分类」）。
 *
 * ⚠️ 2026-09-29 派生（localize）**整块下架**，用户原话「派生这个弄太复杂了，不要了」。
 * （它的上一刀是同日下架「多平台合规版」：「这里的多平台合规版不需要」。）
 *
 * 下架形态的一个直接好处是**判据变量消失了**：原来页面要靠 `input === 'finished'`
 * 区分「从商品信息出图」与「拿成片派生」—— 两类模式的必填项、张数来源、
 * 提示词主线全是反的。现在只剩一类，那个字段、那个类型（`StudioInputKind`）、
 * 那个查询函数（`isDeriveMode`）全部删除：**剩下的每个开关都真的会改变行为**。
 *
 * 为什么形态不能省掉、必须让用户选：
 *   它决定 `app_slug`，而 `app_slug` 是分类/报价/`app_kind` 三处的分叉键 ——
 *   「主图 4 张」与「按平台结构出套图」是**两次不同的计价**，不是同一次的两个参数。
 *   也没法从别的字段派生（「上传了参考版式 = 模仿版式」这种推断，
 *   用户只想改比例却传了张参考图时就错了）。
 */

/** 三个形态。**app_slug 的载体**；顺序 = 页签 1「产出形态」的排列顺序 */
export type StudioModeId = 'main' | 'aplus' | 'copy'

export type StudioMode = {
  id: StudioModeId
  /** 控件上的名字。`aplus` 会被平台档案的 `tabName` 覆盖 */
  name: string
  /** 悬停说明（`title`）—— 一句话说清这个形态产出什么 */
  desc: string
  /**
   * 提交用的 app_slug。**必须与后端已注册的一致**：
   * `detail-*` 是详情页原有键。下架的 `ai-set`（商品套图）/ `detail-long`（长图）
   * 仍在后端与资产分类表里 —— 历史产物要靠它们归类，只是不再有形态能选中。
   */
  appSlug: string
}

/**
 * 全部形态 —— **唯一一份**。详情页与批量页都直接映射它渲染选项，
 * 不许再按任何条件过滤出第二份子集：过滤出来的子集就是第二份真源，
 * 而它失效时不报错，只是「某种图再也选不到了」。
 *
 * 排列顺序按用途成组（主图类 → 详情类），但**不声明分组结构** ——
 * 分组只影响排序，不该再长成一个可点的层级（见文件头第二次收敛）。
 */
export const STUDIO_MODES: StudioMode[] = [
  {
    id: 'main',
    name: '电商主图',
    desc: '白底双角度 + 场景 + 卖点 + 细节，共 4 张，直接上架',
    appSlug: 'detail-main',
  },
  {
    id: 'aplus',
    name: '平台详情模块',
    desc: '按平台官方模块规格出图，逐张匹配官方尺寸（Amazon 即 A+）',
    appSlug: 'detail-aplus',
  },
  {
    id: 'copy',
    name: '模仿版式',
    desc: '上传一张参考版式，换产品不换结构',
    appSlug: 'detail-copy',
  },
]

/** 默认形态 —— 与导航默认落地状态一致（用户从侧栏进来第一眼看到的） */
export const DEFAULT_MODE: StudioModeId = 'main'

export const MODE_IDS: StudioModeId[] = STUDIO_MODES.map((m) => m.id)

/**
 * 按 id 取形态。**认不出就回落默认形态，不抛错** ——
 * 这里的调用方之一是 `lib/appSlug.ts`，它跑在「用户可能手改过地址栏」的路径上，
 * 地址栏里塞个 `?mode=xxx` 不该把整页干崩。
 *
 * 兜底同时兜住了**已下架形态的老链接**：`?mode=long` / `?mode=set` 会落回
 * 「电商主图」，而不是让页面停在一个再也出不了图的形态上
 * （`/ai-set` 那条别名现在也直接落到默认形态，见 `App.tsx`）。
 */
export function getMode(id?: string | null): StudioMode {
  return STUDIO_MODES.find((m) => m.id === id) ?? STUDIO_MODES[0]
}
